import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import toast from 'react-hot-toast'

const BACKEND_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5000/api'
const TOKEN_KEY = 'admin_token'
const REFRESH_TOKEN_KEY = 'admin_refresh_token'

/** Roles that may sign in to this panel. */
export type PanelRole = 'ADMIN' | 'COMPANY_ADMIN'

interface AdminUser {
  id: string
  name: string
  email: string
  role: PanelRole
  avatar?: string
  isSuperAdmin: boolean
  assignedZones: string[]
  /** Set for COMPANY_ADMIN — the operator they run. */
  companyId?: string | null
  companyName?: string | null
  /**
   * True while the account still holds a password an administrator chose for
   * it. The API refuses everything but the rotation until it clears, so the
   * shell sends the user straight there rather than to a wall of 403s.
   */
  mustChangePassword?: boolean
}

const PANEL_ROLES: PanelRole[] = ['ADMIN', 'COMPANY_ADMIN']

/**
 * Load whatever profile the signed-in role is entitled to.
 *
 * Platform admins read /admin/me for their super-admin flag and zones; company
 * admins are refused that endpoint by design and read /company/profile instead,
 * which is also how the panel learns which company to scope itself to.
 */
async function loadPanelProfile(
  role: PanelRole,
  token: string,
  mustChangePassword = false,
): Promise<Pick<AdminUser, 'isSuperAdmin' | 'assignedZones' | 'companyId' | 'companyName'>> {
  // An account still on an issued password is refused every endpoint but the
  // rotation, so asking for the profile would only produce a 403 to swallow.
  if (mustChangePassword) {
    return { isSuperAdmin: false, assignedZones: [], companyId: null, companyName: null }
  }
  const headers = { Authorization: `Bearer ${token}` }
  try {
    if (role === 'COMPANY_ADMIN') {
      const res = await axios.get(`${BACKEND_URL}/company/profile`, { headers })
      const data = res.data?.data ?? res.data
      return {
        isSuperAdmin: false,
        assignedZones: [],
        companyId: data?.company?.id ?? null,
        companyName: data?.company?.name ?? null,
      }
    }
    const res = await axios.get(`${BACKEND_URL}/admin/me`, { headers })
    const profile = res.data?.data ?? res.data
    return {
      isSuperAdmin: profile?.isSuperAdmin ?? false,
      assignedZones: profile?.assignedZones ?? [],
      companyId: null,
      companyName: null,
    }
  } catch {
    // Non-critical: the panel still works, just without the extras.
    return { isSuperAdmin: false, assignedZones: [], companyId: null, companyName: null }
  }
}

interface LoginResponse {
  requiresOtp: boolean
  otpPendingToken: string
  email: string
}

type ResetPhase = 'idle' | 'forgot' | 'otp' | 'newPassword'

interface AuthState {
  user: AdminUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  otpPendingToken: string | null
  otpEmail: string | null
  resetPhase: ResetPhase
  resetOtpPendingToken: string | null
  resetEmail: string | null
  adminLogin: (email: string, password: string) => Promise<LoginResponse | false>
  verifyAdminOtp: (code: string) => Promise<boolean>
  resendAdminOtp: () => Promise<boolean>
  clearOtpState: () => void
  forgotPassword: (email: string) => Promise<boolean>
  verifyResetOtp: (code: string) => Promise<boolean>
  resetPassword: (newPassword: string) => Promise<boolean>
  clearResetState: () => void
  adminLogout: () => void
  clearError: () => void
  /**
   * Keep a new token pair everywhere the panel reads it: localStorage, which
   * every request sends, and this store, which Sign out and the live map read.
   * The refresh interceptor used to write localStorage only, so Sign out sent
   * an expired token (and revoked nothing) and the map's socket connected with
   * it. With no refresh token (an older server) the current one is kept.
   */
  setSessionTokens: (token: string, refreshToken?: string | null) => void
  /**
   * Exchange the administrator-issued password for one of the holder's own.
   *
   * On success the account gains its full surface, so the profile that could
   * not be read at sign-in is loaded here instead.
   */
  completePasswordRotation: (currentPassword: string, newPassword: string) => Promise<boolean>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      otpPendingToken: null,
      otpEmail: null,
      resetPhase: 'idle' as ResetPhase,
      resetOtpPendingToken: null,
      resetEmail: null,

      adminLogin: async (email: string, password: string): Promise<LoginResponse | false> => {
        set({ isLoading: true, error: null })
        try {
          const res = await axios.post(`${BACKEND_URL}/auth/login`, { email, password }, { headers: { 'X-Client-Panel': '1' } })
          const data = res.data.data

          if (data.requiresOtp) {
            set({
              otpPendingToken: data.otpPendingToken,
              otpEmail: data.email,
              isLoading: false,
              error: null,
            })
            return data as LoginResponse
          }

          // Fallback: if backend ever returns tokens directly (shouldn't happen with OTP enabled)
          const { user, token, refreshToken } = data
          if (!PANEL_ROLES.includes(user.role)) {
            set({ isLoading: false, error: 'Access denied. Admin and company accounts only.' })
            return false
          }
          localStorage.setItem('admin_token', token)
          if (refreshToken) localStorage.setItem('admin_refresh_token', refreshToken)

          const profile = await loadPanelProfile(user.role, token, user.mustChangePassword)

          set({
            user: {
              id: user.id,
              name: user.name,
              email: user.email,
              role: user.role,
              mustChangePassword: Boolean(user.mustChangePassword),
              ...profile,
            },
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
          return false
        } catch (err: unknown) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Invalid email or password. Please try again.'
          set({ isLoading: false, error: message })
          return false
        }
      },

      verifyAdminOtp: async (code: string): Promise<boolean> => {
        const { otpPendingToken } = get()
        if (!otpPendingToken) {
          set({ error: 'OTP session expired. Please log in again.' })
          return false
        }

        set({ isLoading: true, error: null })
        try {
          const res = await axios.post(`${BACKEND_URL}/auth/verify-otp`, {
            otpPendingToken,
            code,
          })
          const { user, token, refreshToken } = res.data.data

          if (!PANEL_ROLES.includes(user.role)) {
            set({
              isLoading: false,
              error: 'Access denied. Admin and company accounts only.',
              otpPendingToken: null,
              otpEmail: null,
            })
            toast.error('Access denied. Admin and company accounts only.')
            return false
          }

          localStorage.setItem('admin_token', token)
          if (refreshToken) localStorage.setItem('admin_refresh_token', refreshToken)

          const profile = await loadPanelProfile(user.role, token, user.mustChangePassword)

          set({
            user: {
              id: user.id,
              name: user.name,
              email: user.email,
              role: user.role,
              mustChangePassword: Boolean(user.mustChangePassword),
              ...profile,
            },
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            otpPendingToken: null,
            otpEmail: null,
          })
          return true
        } catch (err: unknown) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Invalid or expired OTP code. Please try again.'

          // If token expired, clear OTP state so user returns to login
          const status = (err as { response?: { status?: number } })?.response?.status
          if (status === 401 || status === 410) {
            set({
              isLoading: false,
              error: 'OTP session expired. Please log in again.',
              otpPendingToken: null,
              otpEmail: null,
            })
            toast.error('OTP session expired. Please log in again.')
          } else {
            set({ isLoading: false, error: message })
            toast.error(message)
          }
          return false
        }
      },

      resendAdminOtp: async (): Promise<boolean> => {
        const { otpPendingToken } = get()
        if (!otpPendingToken) {
          toast.error('OTP session expired. Please log in again.')
          set({ otpPendingToken: null, otpEmail: null })
          return false
        }

        try {
          const res = await axios.post(`${BACKEND_URL}/auth/resend-otp`, { otpPendingToken })
          // Update token if backend returns a new one
          if (res.data.data?.otpPendingToken) {
            set({ otpPendingToken: res.data.data.otpPendingToken })
          }
          toast.success('A new OTP code has been sent to your email.')
          return true
        } catch (err: unknown) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Failed to resend OTP. Please try again.'
          toast.error(message)

          const status = (err as { response?: { status?: number } })?.response?.status
          if (status === 401 || status === 410) {
            set({ otpPendingToken: null, otpEmail: null })
          }
          return false
        }
      },

      clearOtpState: () => {
        set({ otpPendingToken: null, otpEmail: null, error: null })
      },

      forgotPassword: async (email: string): Promise<boolean> => {
        set({ isLoading: true, error: null })
        try {
          const res = await axios.post(`${BACKEND_URL}/auth/forgot-password`, { email })
          const data = res.data.data ?? res.data
          set({
            resetOtpPendingToken: data.otpPendingToken ?? null,
            resetEmail: email,
            resetPhase: 'otp',
            isLoading: false,
            error: null,
          })
          toast.success('Reset code sent to your email.')
          return true
        } catch (err: unknown) {
          // Anti-enumeration: even on error, show success-like message for unknown emails
          const status = (err as { response?: { status?: number } })?.response?.status
          if (status === 429) {
            const message =
              (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
              'Too many requests. Please wait before trying again.'
            set({ isLoading: false, error: message })
            toast.error(message)
            return false
          }
          // For other errors, still transition (anti-enumeration)
          set({
            resetEmail: email,
            resetPhase: 'otp',
            isLoading: false,
            error: null,
          })
          toast.success('If an account exists, a reset code has been sent.')
          return true
        }
      },

      verifyResetOtp: async (code: string): Promise<boolean> => {
        const { resetOtpPendingToken } = get()
        if (!resetOtpPendingToken) {
          set({ error: 'Reset session expired. Please start over.', resetPhase: 'idle', resetOtpPendingToken: null, resetEmail: null })
          return false
        }

        set({ isLoading: true, error: null })
        try {
          const res = await axios.post(`${BACKEND_URL}/auth/verify-otp`, {
            otpPendingToken: resetOtpPendingToken,
            code,
          })
          const data = res.data.data ?? res.data
          set({
            resetOtpPendingToken: data.otpPendingToken ?? resetOtpPendingToken,
            resetPhase: 'newPassword',
            isLoading: false,
            error: null,
          })
          return true
        } catch (err: unknown) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Invalid or expired code. Please try again.'
          const status = (err as { response?: { status?: number } })?.response?.status
          if (status === 401 || status === 410) {
            set({ isLoading: false, error: 'Reset session expired. Please start over.', resetPhase: 'idle', resetOtpPendingToken: null, resetEmail: null })
            toast.error('Reset session expired. Please start over.')
          } else {
            set({ isLoading: false, error: message })
            toast.error(message)
          }
          return false
        }
      },

      resetPassword: async (newPassword: string): Promise<boolean> => {
        const { resetOtpPendingToken } = get()
        if (!resetOtpPendingToken) {
          set({ error: 'Reset session expired. Please start over.', resetPhase: 'idle', resetOtpPendingToken: null, resetEmail: null })
          return false
        }

        set({ isLoading: true, error: null })
        try {
          await axios.post(`${BACKEND_URL}/auth/reset-password-otp`, {
            otpPendingToken: resetOtpPendingToken,
            newPassword,
          })
          set({
            resetPhase: 'idle',
            resetOtpPendingToken: null,
            resetEmail: null,
            isLoading: false,
            error: null,
          })
          toast.success('Password reset successful! Please sign in with your new password.')
          return true
        } catch (err: unknown) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Failed to reset password. Please try again.'
          set({ isLoading: false, error: message })
          toast.error(message)
          return false
        }
      },

      clearResetState: () => {
        set({ resetPhase: 'idle', resetOtpPendingToken: null, resetEmail: null, error: null })
      },

      adminLogout: () => {
        // Revoke on the server as well. Clearing localStorage alone left the
        // access token good for seven days and the refresh token for thirty.
        const token = currentAccessToken()
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
        if (token) {
          axios
            .post(`${BACKEND_URL}/auth/logout`, refreshToken ? { refreshToken } : {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 })
            .catch(() => { /* the local session is gone either way */ })
        }
        localStorage.removeItem('admin_token')
        localStorage.removeItem('admin_refresh_token')
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
          otpPendingToken: null,
          otpEmail: null,
          resetPhase: 'idle' as ResetPhase,
          resetOtpPendingToken: null,
          resetEmail: null,
        })
      },

      completePasswordRotation: async (currentPassword: string, newPassword: string): Promise<boolean> => {
        const { user } = get()
        const token = currentAccessToken()
        if (!token || !user) {
          set({ error: 'Your session expired. Please sign in again.' })
          return false
        }

        set({ isLoading: true, error: null })
        try {
          // rotateSession: this panel keeps the fresh pair returned below, so
          // the server may end this session with the others (see Settings).
          const res = await axios.post(
            `${BACKEND_URL}/auth/change-password`,
            { currentPassword, newPassword, rotateSession: true },
            { headers: { Authorization: `Bearer ${token}` } },
          )

          // The server ends every session from before the change, this one
          // included, and answers with a fresh pair. Kept here, or the profile
          // read below and every request after it would be refused.
          const fresh = res.data?.data ?? {}
          if (fresh.token) get().setSessionTokens(fresh.token, fresh.refreshToken)
          const profile = await loadPanelProfile(user.role, fresh.token ?? token)
          set({
            user: { ...user, ...profile, mustChangePassword: false },
            isLoading: false,
            error: null,
          })
          toast.success('Password set. Welcome in.')
          return true
        } catch (err: unknown) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Failed to set your password. Please try again.'
          set({ isLoading: false, error: message })
          toast.error(message)
          return false
        }
      },

      clearError: () => set({ error: null }),

      setSessionTokens: (token: string, refreshToken?: string | null) => {
        localStorage.setItem(TOKEN_KEY, token)
        if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
        set({ token })
      },
    }),
    {
      name: 'admin-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

/**
 * The newest access token. localStorage first: it is what every request sends,
 * and a refresh in another tab lands there without reaching this tab's store.
 */
export function currentAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY) ?? useAuthStore.getState().token
}
